import * as React from 'react'
import { FileText } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu'
import { SourceDocChip } from './SourceDocChip'
import { sourceFileBasename } from './source-doc-kind'
import type { SourceDocRef } from './useSourceDocViewer'

export type SourceDocEndpoint = {
  label: string
  sourceFile?: string
  sourcePage?: number
  sourceExcerpt?: string
}

export interface SourceDocEndpointsChipProps {
  jobId?: string
  endpoints: readonly SourceDocEndpoint[]
  onOpen: (args: SourceDocRef) => void
}

/** Distinct endpoint docs by path; identical paths collapse to one chip. */
export function distinctSourceEndpoints(
  endpoints: readonly SourceDocEndpoint[]
): SourceDocEndpoint[] {
  const seen = new Set<string>()
  const out: SourceDocEndpoint[] = []
  for (const endpoint of endpoints) {
    const file = endpoint.sourceFile
    if (!file || seen.has(file)) continue
    seen.add(file)
    out.push(endpoint)
  }
  return out
}

/**
 * Compact chip for one document, or a two-item menu when endpoints cite
 * different source files (ingest-tree lateral rows).
 */
export function SourceDocEndpointsChip({
  jobId,
  endpoints,
  onOpen
}: SourceDocEndpointsChipProps): React.ReactElement | null {
  if (!jobId) return null
  const docs = distinctSourceEndpoints(endpoints)
  if (docs.length === 0) return null
  if (docs.length === 1) {
    const [only] = docs
    return (
      <SourceDocChip
        compact
        jobId={jobId}
        sourceFile={only.sourceFile}
        sourcePage={only.sourcePage}
        sourceExcerpt={only.sourceExcerpt}
        onOpen={onOpen}
      />
    )
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex items-center gap-1 rounded-full border bg-bg-elev px-1.5 py-0.5 text-10 font-medium text-ink hover:border-brand/40 hover:text-brand"
          aria-label="Open source document"
        >
          <FileText aria-hidden className="size-3 shrink-0" />
          <span className="font-mono tabular-nums">{docs.length}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        {docs.map((doc) => (
          <DropdownMenuItem
            key={doc.sourceFile}
            className="cursor-pointer gap-2 text-xs"
            onSelect={() => {
              if (!doc.sourceFile) return
              onOpen({
                jobId,
                sourceFile: doc.sourceFile,
                sourcePage: doc.sourcePage,
                sourceExcerpt: doc.sourceExcerpt
              })
            }}
          >
            <span className="shrink-0 text-muted-foreground">{doc.label}</span>
            <span className="min-w-0 truncate font-mono">
              {sourceFileBasename(doc.sourceFile!)}
              {doc.sourcePage !== undefined ? ` · p. ${doc.sourcePage}` : ''}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
